import React, {MouseEvent, ReactNode, Fragment} from 'react';
import {observer} from 'mobx-react';
import {formatValue, percentFormat} from '../../utility/FormatValueUtils';
import {classes, stylesheet} from 'typestyle';
import {important, px} from 'csx';
import configString from '../../utility/Config';
import {CellAttributeContextMenu, DataKey, SelectorTableColumnDef} from '../../stores/GeneralSelectorTableStore';
import {AnalyticsTab, Store} from '../../stores/store';
import {getElementAbsolutePosition} from '../../utility/DomUtils';
import {isKPI, KPI} from '../../kpi';
import {TProvider} from '../../i18n/i18n';
import _ from 'lodash';
import {ROW_HEIGHT} from './TableWrapper';
import {ChannelDataRegister} from '../../types/api/tableData/TableDataForChannel';
import {CompareMode, RatioResultItem} from '../../stores/CompareStore';
import {observable} from 'mobx';
import {uniquePairsFromArray} from '../../utility/HelperUtils';
import {
    BlackColor,
    BlackColorFaded10,
    BlueColorFaded20,
    ChartableCellWithContextMenuColor,
    LightBlueBackgroundColor,
    Shadow8,
    TableCellRateColor50,
    TableCellRateColor80,
    WhiteColor,
} from '../../styles/colors';
import CockpitIcon from './CockpitIcon';

interface SpecialActionTableCellProps {
    store: Store;
    chartabilityIsAllowed?: boolean;
    rowIndex: number;
    columnDef: SelectorTableColumnDef;
    rowValues: {[key: string]: any};
    isChannel?: boolean;
}

export enum DraggedCellMode {
    CHANNEL = 'channel',
    SEARCH_PRODUCT = 'searchProduct',
}

export interface DraggedTableCellData {
    draggedCellMode: DraggedCellMode;
    primary: string;
    secondary: KPI;
    title: string;
    compareMode?: CompareMode;
    compareTarget?: string;
}

export const isDraggedCellMode = (value: any): value is DraggedCellMode => {
    return Object.values(DraggedCellMode).some((mode) => mode === value);
};

@observer
export default class SpecialActionTableCell extends React.Component<SpecialActionTableCellProps> {
    @observable private menuButtonIsVisible = false;
    @observable private textIsRevealed = false;

    private handleRowDragStart(
        event: React.DragEvent<HTMLElement>,
        draggedCellMode: DraggedCellMode,
        primaryAttribute: string,
        secondaryAttribute: DataKey,
        titleAttribute: string
    ): void {
        if (!isKPI(secondaryAttribute)) {
            throw Error('Cannot create diagram with none KPI attribute ' + secondaryAttribute);
        }

        const {columnDef} = this.props;
        const compareMode = columnDef.compareColumnInfo ? columnDef.compareColumnInfo.compareMode : undefined;
        const compareTarget = columnDef.compareColumnInfo ? columnDef.compareColumnInfo.targetName : undefined;

        const draggedTableCellData: DraggedTableCellData = {
            draggedCellMode,
            primary: primaryAttribute,
            secondary: secondaryAttribute,
            title: titleAttribute,
            compareMode,
            compareTarget: compareMode === CompareMode.ChannelOrVariantColumn ? compareTarget : undefined,
        };
        event.dataTransfer.setData('draggedCellData', JSON.stringify(draggedTableCellData));
        this.props.store.lineDiagramStore.coloredDropBoxesAreVisible = true;
    }

    private itemDragEnded = (event) => {
        this.props.store.lineDiagramStore.coloredDropBoxesAreVisible = false;
    };

    private openContextMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (
            this.props.isChannel ||
            this.props.store.compareStore.compareIsEnabled ||
            this.props.store.activeAnalyticsTab === AnalyticsTab.CampaignView
        ) {
            return;
        }

        const dropdownTarget =
            event.currentTarget.className.indexOf('menu-button') === -1 ? event.currentTarget : event.currentTarget.parentNode.parentNode;
        const rect = getElementAbsolutePosition(dropdownTarget);
        const {
            columnDef,
            store: {selectorTableStore},
        } = this.props;

        if (!isKPI(columnDef.dataKey) || columnDef.sessionApiKey === undefined) {
            return;
        }

        const contextMenuInfo: CellAttributeContextMenu = {
            x: rect.left,
            y: rect.top + 6,
            channelCompareMode: columnDef.compareColumnInfo ? columnDef.compareColumnInfo.compareMode : undefined,
            compareChannel:
                columnDef.compareColumnInfo && columnDef.compareColumnInfo.targetName ? columnDef.compareColumnInfo.targetName : undefined,
            tab: this.props.store.activeAnalyticsTab,
            primary: undefined,
            secondary: columnDef.dataKey,
            sessionApiKey: columnDef.sessionApiKey,
        };

        if (this.props.store.activeAnalyticsTab === AnalyticsTab.SearchView) {
            contextMenuInfo.primary = this.props.rowValues.searchterm;
        } else {
            contextMenuInfo.primary = this.props.rowValues.productId;
        }
        selectorTableStore.cellAttributeContextMenu = contextMenuInfo;
    };

    private showOptionButton = () => {
        const {
            columnDef,
            store: {compareStore},
        } = this.props;

        if (columnDef.sessionApiKey === undefined || compareStore.compareIsEnabled) {
            return;
        }

        if (this.props.isChannel) {
            return;
        }

        this.menuButtonIsVisible = true;
    };

    private hideOptionButton = () => {
        if (this.props.isChannel) {
            return;
        }

        this.menuButtonIsVisible = false;
    };

    private contextMenuVisibleForCell() {
        const {
            columnDef,
            store: {selectorTableStore},
        } = this.props;
        const contextMenu = selectorTableStore.cellAttributeContextMenu;

        const channelCompareMode = columnDef.compareColumnInfo ? columnDef.compareColumnInfo.compareMode : undefined;
        const compareChannel =
            columnDef.compareColumnInfo && columnDef.compareColumnInfo.targetName ? columnDef.compareColumnInfo.targetName : undefined;

        if (this.props.store.activeAnalyticsTab === AnalyticsTab.SearchView) {
            if (
                contextMenu !== null &&
                contextMenu.primary === this.props.rowValues.searchterm &&
                contextMenu.secondary === columnDef.dataKey &&
                contextMenu.compareChannel === compareChannel &&
                contextMenu.channelCompareMode === channelCompareMode
            ) {
                return true;
            }
        } else if (
            contextMenu !== null &&
            contextMenu.primary === this.props.rowValues.productId &&
            contextMenu.secondary === columnDef.dataKey &&
            contextMenu.compareChannel === compareChannel &&
            contextMenu.channelCompareMode === channelCompareMode
        ) {
            return true;
        }
        return false;
    }

    private getOpacityBasedOnSlopeScore(score) {
        if (score >= 1.2) {
            return 1;
        } else if (score >= 1.1) {
            return 0.9;
        } else if (score >= 1) {
            return 0.8;
        } else if (score >= 0.9) {
            return 0.7;
        } else if (score >= 0.8) {
            return 0.6;
        } else if (score >= 0.7) {
            return 0.5;
        } else if (score >= 0.6) {
            return 0.4;
        } else if (score >= 0.5) {
            return 0.3;
        } else if (score >= 0.4) {
            return 0.2;
        } else if (score >= 0.3) {
            return 0.1;
        } else {
            return 0.0;
        }
    }

    private getOpacityBasedOnOutlierScore(score) {
        if (score > 1.3) {
            return 1;
        } else if (score > 1.2) {
            return 0.9;
        } else if (score > 1.1) {
            return 0.8;
        } else if (score > 1) {
            return 0.7;
        } else if (score > 0.9) {
            return 0.6;
        } else if (score > 0.8) {
            return 0.5;
        } else if (score > 0.7) {
            return 0.4;
        } else if (score > 0.6) {
            return 0.3;
        } else if (score > 0.5) {
            return 0.2;
        } else if (score > 0.4) {
            return 0.1;
        } else {
            return 0.0;
        }
    }

    private renderIrregularityIcon() {
        const thresholdOutlier = 0.4;
        const thresholdSlope = 0.3;
        let outlierScore = 0;
        let slopeScore = 0;

        if (configString('ANALYTICS_UI_DEBUG', '0') === '1') {
            outlierScore = Math.random() > 0.5 ? Math.random() : 0;
            slopeScore = Math.random() * 2 - 1;
        } else {
            const scoreAttributes = this.props.columnDef.scoreAttributes;
            if (scoreAttributes) {
                outlierScore = this.props.rowValues[scoreAttributes.outlier] || 0;
                slopeScore = this.props.rowValues[scoreAttributes.slope] || 0;
            }
        }

        if (outlierScore > thresholdOutlier) {
            return (
                <TProvider>
                    {(t) => (
                        <div
                            key="max"
                            className={cellCss.outlierSlopeIcon}
                            title={t('table:outlierDetected')}
                            style={{color: `rgba(0,0,0, ${this.getOpacityBasedOnOutlierScore(outlierScore)})`}}>
                            <i className="fa fa-bolt" aria-hidden="true" />
                        </div>
                    )}
                </TProvider>
            );
        }

        if (Math.abs(slopeScore) > thresholdSlope) {
            const slopeUp = slopeScore > 0;
            return (
                <div
                    key="max"
                    className={cellCss.outlierSlopeIcon}
                    style={{
                        color: `rgba(0,0,0, ${this.getOpacityBasedOnSlopeScore(Math.abs(slopeScore))})`,
                        transform: `rotate(${slopeUp ? 45 : -45}deg)`,
                    }}>
                    <i className="fa fa-long-arrow-right" aria-hidden="true" />
                </div>
            );
        }

        return null;
    }

    private renderRevealable(value) {
        const {columnDef} = this.props;

        let outerStyle;
        let innerStyle;
        if (this.textIsRevealed) {
            outerStyle = {};
            innerStyle = {
                display: 'inline-block',
                boxShadow: Shadow8,
                backgroundColor: WhiteColor,
                whiteSpace: 'nowrap',
                marginLeft: columnDef.align === 'right' ? 0 : -5,
                marginRight: columnDef.align === 'right' ? -5 : 0,
                padding: '0 5px',
                zIndex: 33,
            };
        } else {
            outerStyle = {
                overflow: 'hidden',
                textAlign: columnDef.align,
                width: columnDef.width - 10,
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                zIndex: 33,
            };
            innerStyle = {display: 'inline-block'};
        }
        return (
            <div style={outerStyle}>
                <div
                    style={innerStyle}
                    onMouseEnter={(event: MouseEvent<HTMLDivElement>) => {
                        const el = event.target as Element;
                        if (el.clientWidth >= columnDef.width - 10) {
                            this.textIsRevealed = true;
                        }
                    }}
                    onMouseLeave={() => (this.textIsRevealed = false)}>
                    {value}
                </div>
            </div>
        );
    }

    private renderChartable(value, chartabilityIsAllowed: boolean) {
        const {store, columnDef, rowValues, isChannel} = this.props;

        let linkDivClass = cellCss.baseLink;
        if (!chartabilityIsAllowed) {
            linkDivClass = classes(cellCss.notChartableCell, linkDivClass);
        } else if (columnDef.sessionApiKey !== undefined && !columnDef.compareColumnInfo) {
            linkDivClass = classes(cellCss.chartableCellWithContextMenu, linkDivClass);
        }

        let primary;
        let titleForLegend;
        let apiCall;
        if (isChannel) {
            apiCall = DraggedCellMode.CHANNEL;
            const {selectedChannel} = this.props.store.channelStore;
            primary = selectedChannel ? selectedChannel.name : undefined;
            titleForLegend = store.activeAnalyticsTab === AnalyticsTab.CampaignView ? 'table:campaigns' : '';
        } else if (store.activeAnalyticsTab === AnalyticsTab.SearchView) {
            apiCall = DraggedCellMode.SEARCH_PRODUCT;
            primary = rowValues.searchterm;
            titleForLegend = '';
        } else if (store.activeAnalyticsTab === AnalyticsTab.CampaignView) {
            apiCall = DraggedCellMode.SEARCH_PRODUCT;
            primary = rowValues.id;
            titleForLegend = rowValues.name;
        } else {
            apiCall = DraggedCellMode.SEARCH_PRODUCT;
            primary = rowValues.productId;
            titleForLegend = rowValues.title || '';
        }

        return (
            <div className={cellCss.chartableCellContainer}>
                <a
                    className={linkDivClass}
                    draggable={chartabilityIsAllowed}
                    onDragEnd={this.itemDragEnded}
                    onDragStart={(e) => this.handleRowDragStart(e, apiCall, primary, columnDef.dataKey, titleForLegend)}
                    href="javascript:void(0)">
                    {value}
                </a>
            </div>
        );
    }

    private checkIfChartabilityIsAllowed(): boolean {
        if (this.props.chartabilityIsAllowed !== undefined) {
            return this.props.chartabilityIsAllowed;
        }
        return true;
    }

    private generateChannelCompareTitleForDiffColumns(): string {
        const {
            columnDef,
            rowValues,
            store: {compareStore},
        } = this.props;

        if (columnDef.compareColumnInfo && columnDef.compareColumnInfo.compareMode !== CompareMode.AbsDiffMode) {
            return '';
        }

        const valueFormat = (value: number) => formatValue(value, columnDef.valueFormat);

        const channelCompareRegister = rowValues.channelCompareRegister;
        if (
            channelCompareRegister &&
            compareStore.compareIsEnabled &&
            compareStore.checkIfColumnShouldBeAffectedByChannelComparison(columnDef.dataKey)
        ) {
            const channelCombinations = uniquePairsFromArray(Object.keys(channelCompareRegister));
            return channelCombinations
                .map((channelPackagePackage) => {
                    const channelsWithValues = channelPackagePackage
                        .map((channel) => ({channel, value: channelCompareRegister[channel][columnDef.dataKey]}))
                        .sort((a, b) => b.value - a.value);
                    return `${channelsWithValues[0].channel} - ${channelsWithValues[1].channel} -> ${valueFormat(
                        channelsWithValues[0].value
                    )} - ${valueFormat(channelsWithValues[1].value)} = ${valueFormat(
                        Math.abs(channelsWithValues[0].value - channelsWithValues[1].value)
                    )}`;
                })
                .join('\n');
        }
        return '';
    }

    private renderChannelCompareColorLines(): React.ReactNode[] {
        const {
            store: {compareStore},
            columnDef,
            rowValues,
        } = this.props;
        const {getChannelColor} = compareStore;

        if (
            !columnDef.compareColumnInfo ||
            !rowValues.channelCompareRegister ||
            columnDef.channelComparisonEnabled === false ||
            !compareStore.compareIsEnabled
        ) {
            return [];
        }

        if (columnDef.compareColumnInfo && columnDef.compareColumnInfo.compareMode !== CompareMode.AbsDiffMode) {
            return [];
        }

        const compareChannelNames = Object.keys(rowValues.channelCompareRegister);

        const channelsAndValues = compareChannelNames
            .map((channel) => ({
                channel,
                value: rowValues.channelCompareRegister[channel][columnDef.dataKey],
            }))
            .sort((a, b) => b.value - a.value);

        if (channelsAndValues.length === 2) {
            return [<ChannelColorLine key={0} color={getChannelColor(channelsAndValues[0].channel, compareChannelNames).toString()} />];
        }

        return [
            <ChannelColorLine key={1} color={getChannelColor(channelsAndValues[0].channel, compareChannelNames).toString()} />,
            <ChannelColorLine
                key={2}
                color={getChannelColor(channelsAndValues[channelsAndValues.length - 1].channel, compareChannelNames).toString()}
            />,
        ];
    }

    private renderAbsoluteLeftCellArea() {
        const {rowValues} = this.props;

        let leftCellAreaContent: ReactNode[] = [];

        if (rowValues.channelCompareRegister) {
            leftCellAreaContent = this.renderChannelCompareColorLines();
        } else if (this.menuButtonIsVisible) {
            leftCellAreaContent = [
                <div key="insightsMenuButton" className={classes(cellCss.menuButton, 'menu-button')} onClick={this.openContextMenu}>
                    <i className="fa fa-ellipsis-v" />
                </div>,
            ];
        } else {
            leftCellAreaContent = [
                <div className={cellCss.slopeOutlierIconContainer} key="slopeOutlierIcon">
                    {this.renderIrregularityIcon()}
                </div>,
            ];
        }

        return <div className={cellCss.absoluteLeftCellArea}>{leftCellAreaContent}</div>;
    }

    private generateRatioOpacityFromMaxAbsDiff(channelCompareRegister: ChannelDataRegister, availableChannelNames: string[], dataKey: DataKey) {
        const maxAbsDiff = this.calculateDiffCellValueForChannelCompareMode(availableChannelNames, channelCompareRegister, dataKey);

        if (maxAbsDiff > 1000) {
            return 1;
        }
        if (maxAbsDiff > 900) {
            return 0.9;
        }
        if (maxAbsDiff > 700) {
            return 0.8;
        }
        if (maxAbsDiff > 500) {
            return 0.7;
        }
        if (maxAbsDiff > 300) {
            return 0.6;
        }
        if (maxAbsDiff > 200) {
            return 0.5;
        }
        if (maxAbsDiff > 100) {
            return 0.4;
        }
        return 0.3;
    }

    private calculateDiffCellValueForChannelCompareMode(
        channelNames: string[],
        channelCompareRegister: ChannelDataRegister,
        dataKey: DataKey
    ): number {
        const valuesPerChannel = channelNames.map((channel) => channelCompareRegister[channel][dataKey]);
        const max = _.max(valuesPerChannel);
        const min = _.min(valuesPerChannel);
        return max - min;
    }

    private getChannelCompareValuesIfPossible(): {
        cellValue: string | number;
        customCellComponent: React.ReactNode;
    } {
        const {
            columnDef,
            rowValues,
            store: {compareStore},
        } = this.props;

        const {compareIsEnabled} = compareStore;
        let cellValue = rowValues[columnDef.dataKey];
        let customCellComponent: ReactNode | null = null;

        if (compareIsEnabled && rowValues.channelCompareRegister && columnDef.channelComparisonEnabled !== false) {
            const channelCompareRegister: ChannelDataRegister = rowValues.channelCompareRegister;
            if (channelCompareRegister && columnDef.compareColumnInfo) {
                const availableChannelNames = Object.keys(channelCompareRegister);
                switch (columnDef.compareColumnInfo.compareMode) {
                    case CompareMode.AbsDiffMode:
                        cellValue = this.calculateDiffCellValueForChannelCompareMode(
                            availableChannelNames,
                            channelCompareRegister,
                            columnDef.dataKey
                        );
                        break;
                    case CompareMode.ChannelOrVariantColumn:
                        if (
                            columnDef.compareColumnInfo.targetName &&
                            channelCompareRegister[columnDef.compareColumnInfo.targetName] &&
                            channelCompareRegister[columnDef.compareColumnInfo.targetName][columnDef.dataKey]
                        ) {
                            cellValue = channelCompareRegister[columnDef.compareColumnInfo.targetName][columnDef.dataKey];
                        }
                        break;
                    case CompareMode.RatioMode:
                        const ratioResultItems = compareStore.calculateRatioValues(channelCompareRegister, columnDef.dataKey);
                        customCellComponent = (
                            <RatioCell
                                opacity={this.generateRatioOpacityFromMaxAbsDiff(channelCompareRegister, availableChannelNames, columnDef.dataKey)}
                                ratioResultItems={ratioResultItems}
                                dataKey={columnDef.dataKey}
                                channelCompareRegister={channelCompareRegister}
                            />
                        );
                        break;
                    default:
                        break;
                }
            }
        }
        return {cellValue, customCellComponent};
    }

    public render() {
        const {columnDef, rowValues, isChannel, rowIndex} = this.props;
        const chartabilityIsAllowed = this.checkIfChartabilityIsAllowed();
        const cellOpacity = 1;

        const {cellValue, customCellComponent} = this.getChannelCompareValuesIfPossible();

        if (customCellComponent) {
            return customCellComponent;
        }

        const formattedValue = formatValue(cellValue, columnDef.valueFormat);

        let value: string | JSX.Element = formattedValue;

        if (columnDef.withRevealOnHover) {
            value = this.renderRevealable(value);
        }
        if (columnDef.isChartable) {
            value = this.renderChartable(value, chartabilityIsAllowed);
        }
        if (columnDef.align === 'right' && !this.textIsRevealed) {
            value = <div className={cellCss.rightAligned}>{value}</div>;
        }

        if (rowIndex > 0 && columnDef.onIconClick) {
            const onClick = columnDef.onIconClick;

            value = (
                <Fragment>
                    {value}
                    <div className={cellCss.cockpitLink + ' cockpitLink'} onClick={() => onClick(formattedValue)}>
                        <CockpitIcon />
                    </div>
                </Fragment>
            );
        }

        if (!isChannel && columnDef.isRate) {
            value = (
                <div title={this.generateChannelCompareTitleForDiffColumns()}>
                    <div
                        className={cellCss.rateBar}
                        style={{
                            width: Math.min(1, rowValues[columnDef.dataKey]) * 100 + '%',
                        }}
                    />
                    {value}
                </div>
            );
        }

        return (
            <div
                className={classes(cellCss.cellBackground, this.contextMenuVisibleForCell() ? 'menu-active' : undefined)}
                style={{opacity: cellOpacity, overflow: columnDef.withRevealOnHover ? 'visible' : 'hidden'}}
                title={this.generateChannelCompareTitleForDiffColumns()}
                onMouseOver={this.showOptionButton}
                onMouseLeave={this.hideOptionButton}
                onContextMenu={this.openContextMenu}>
                {this.renderAbsoluteLeftCellArea()}
                {value}
            </div>
        );
    }
}

class ChannelColorLine extends React.Component<{color: string}> {
    public render() {
        return <div className={cellCss.channelColorItem} style={{backgroundColor: this.props.color}} />;
    }
}

class RatioBar extends React.Component<{ratioResultItem: RatioResultItem; dataKey: DataKey; channelCompareRegister: ChannelDataRegister}> {
    public render() {
        const {ratioResultItem, dataKey, channelCompareRegister} = this.props;
        return (
            <div
                title={
                    ratioResultItem.channel +
                    ' ' +
                    formatValue(ratioResultItem.percentage, percentFormat()) +
                    ' ' +
                    channelCompareRegister[ratioResultItem.channel][dataKey]
                }
                key={ratioResultItem.channel}
                className={cellCss.absoluteColorCellItem}
                style={{
                    backgroundColor: ratioResultItem.color.toString(),
                    width: `${ratioResultItem.percentage * 100}%`,
                }}
            />
        );
    }
}

const RatioCell: React.FunctionComponent<{
    opacity: number;
    ratioResultItems: RatioResultItem[];
    dataKey: DataKey;
    channelCompareRegister: ChannelDataRegister;
}> = observer(({opacity, ratioResultItems, dataKey, channelCompareRegister}) => {
    return (
        <div style={{height: ROW_HEIGHT}}>
            <div className={cellCss.absoluteColorCell} style={{opacity}}>
                {ratioResultItems.map((ratioValue) => (
                    <RatioBar
                        key={ratioValue.channel}
                        dataKey={dataKey}
                        channelCompareRegister={channelCompareRegister}
                        ratioResultItem={ratioValue}
                    />
                ))}
            </div>
        </div>
    );
});

const cellCss = stylesheet({
    absoluteColorCellItem: {
        display: 'inline-block',
        height: '100%',
        opacity: 0.7,
    },
    absoluteColorCell: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    channelColorItem: {
        display: 'inline-block',
        verticalAlign: 'top',
        width: 4,
        height: '100%',
        marginRight: 1,
    },
    absoluteLeftCellArea: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        opacity: 0.5,
        zIndex: 33,
    },
    outlierSlopeIcon: {
        fontSize: px(13),
    },
    slopeOutlierIconContainer: {
        paddingLeft: 6,
    },
    menuButton: {
        cursor: 'pointer',
        background: BlackColorFaded10,
        textAlign: 'center',
        width: 17,
        display: 'inline-block',
    },
    chartableCellContainer: {
        position: 'relative',
        textAlign: 'right',
    },
    contextMenu: {
        position: 'relative',
        textAlign: 'right',
    },
    chartableCellWithContextMenu: {
        color: ChartableCellWithContextMenuColor,
    },
    baseLink: {
        whiteSpace: 'nowrap',
    },
    notChartableCell: {
        cursor: important('default'),
        color: important(BlackColor),
        $nest: {
            '&:hover': {textDecoration: 'none'},
        },
    },
    rightAligned: {
        float: 'right',
        position: 'relative',
    },
    rateBar: {
        backgroundColor: TableCellRateColor50,
        borderBottom: '1px solid ' + TableCellRateColor80,
        position: 'absolute',
        right: 0,
        top: 5,
        bottom: 5,
    },
    cellBackground: {
        position: 'absolute',
        paddingRight: 5,
        paddingLeft: 5,
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        $nest: {
            '&.menu-active': {
                backgroundColor: BlueColorFaded20,
                color: important(WhiteColor),
            },
            '&:hover .cockpitLink': {
                opacity: 1,
            },
        },
    },
    cockpitLink: {
        position: 'absolute',
        left: -25,
        top: 0,
        bottom: 0,
        cursor: 'pointer',
        background: LightBlueBackgroundColor,
        opacity: 0,
    },
});



// WEBPACK FOOTER //
// ./src/views/Table/SpecialActionTableCell.tsx